import { EffectCallback, useCallback, useEffect, useMemo, useRef, useState } from "react";

export const useEffectOnce = (effect: EffectCallback) => {
  // eslint-disable-next-line react-hooks/exhaustive-deps
  useEffect(effect, []);
};

export function useMemoOnce<T>(factory: () => T): T {
  // eslint-disable-next-line react-hooks/exhaustive-deps
  return useMemo(factory, []);
};

const arraysAreEqual = <T extends any[]>(a: T, b: T) => {
  if (a.length !== b.length) return false;

  for (let i = 0; i < a.length; i++) {
    if (!Object.is(a[i], b[i])) return false;
  }

  return true;
};

export function useStableArray<T extends any[]>(unstable: T): T {
  const lastMemoizedArrayRef = useRef<T>(unstable);

  // we have been passed the same array exactly
  if (Object.is(unstable, lastMemoizedArrayRef.current)) {
    return lastMemoizedArrayRef.current;
  }

  // array is different so check length/contents for object equality
  if (arraysAreEqual(unstable, lastMemoizedArrayRef.current)) {
    return lastMemoizedArrayRef.current;
  }

  // different contents so update underlying ref
  lastMemoizedArrayRef.current = unstable;
  return unstable;
}

export function useCallbackOnce<T extends Function>(callback: T): T {
  // eslint-disable-next-line react-hooks/exhaustive-deps
  return useCallback(callback, []);
};

export function useGetSet<T>(initialState: T | (() => T)): [() => T, (newState: T | ((prevState: T) => T)) => void] {
  // use state internally but track it in a ref for the getter
  const [state, setState] = useState<T>(initialState);
  const stateRef = useRef<T>(state);
  useEffect(() => {
    stateRef.current = state;
  }, [state]);

  // these should never update and therefore not cause effects to fire when in dep list
  const get = useCallback((): T => stateRef.current, []);
  const set = useCallback((newState: T | ((prevState: T) => T)): void => {
    const nextState = typeof newState === 'function'
      ? (newState as ((prevState: T) => T))(stateRef.current)
      : newState;

    stateRef.current = nextState; // I know this runs in the effect but the effect can be delayed, do not remove.
    setState(nextState);
  }, []);

  return [get, set];
}
